import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { RootState } from '@/store';
import { loginUser, clearError } from '@/store/slices/authSlice';
import { ROUTES } from '@/utils/constants';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { executePendingAction } from '@/utils/pendingActions';
import { fadeInUp } from '@/utils/animations';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState<{ email?: string; password?: string }>({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  const from =
    location.state?.from?.pathname ||
    sessionStorage.getItem('redirectAfterLogin') ||
    ROUTES.PRODUCTS;

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!email.includes('@')) {
      errors.email = 'Invalid email address';
    }

    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        await dispatch(loginUser({ email, password })).unwrap();

        const userFromStorage = localStorage.getItem('user');
        if (userFromStorage) {
          try {
            const user = JSON.parse(userFromStorage);
            if (!localStorage.getItem('selectedRole') && (user.role === 'user' || !user.role)) {
              localStorage.setItem('selectedRole', 'buyer');
            }
          } catch (e) {
            if (!localStorage.getItem('selectedRole')) {
              localStorage.setItem('selectedRole', 'buyer');
            }
          }
        } else if (!localStorage.getItem('selectedRole')) {
          localStorage.setItem('selectedRole', 'buyer');
        }

        const hasPendingAction = await executePendingAction();
        const redirectPath = sessionStorage.getItem('redirectAfterLogin') || from || ROUTES.PRODUCTS;
        sessionStorage.removeItem('redirectAfterLogin');

        if (hasPendingAction) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }

        navigate(redirectPath, { replace: true });
      } catch (error) {
        console.error('Login error:', error);
      }
    }
  };

  return (
    <motion.div initial="initial" animate="animate" variants={fadeInUp}>
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Welcome back</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Don't have an account?{' '}
          <Link
            to={ROUTES.SIGNUP}
            className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            Sign up for free
          </Link>
        </p>
      </div>

      {/* Form */}
      <form className="space-y-6" onSubmit={handleSubmit} noValidate>
        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
            <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (formErrors.email) setFormErrors({ ...formErrors, email: undefined });
            }}
            className={cn(
              'w-full h-12 px-4 rounded-lg',
              'bg-gray-50 dark:bg-dark-700',
              'border-2 transition-colors',
              formErrors.email
                ? 'border-error-500 focus:border-error-500'
                : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
              'text-gray-900 dark:text-white placeholder-gray-500',
              'focus:outline-none focus:ring-0'
            )}
            placeholder="name@example.com"
          />
          {formErrors.email && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.email}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (formErrors.password) setFormErrors({ ...formErrors, password: undefined });
              }}
              className={cn(
                'w-full h-12 px-4 pr-12 rounded-lg',
                'bg-gray-50 dark:bg-dark-700',
                'border-2 transition-colors',
                formErrors.password
                  ? 'border-error-500 focus:border-error-500'
                  : 'border-gray-300 dark:border-dark-600 focus:border-primary-500',
                'text-gray-900 dark:text-white placeholder-gray-500',
                'focus:outline-none focus:ring-0'
              )}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          {formErrors.password && (
            <p className="mt-2 text-sm text-error-600 dark:text-error-400">{formErrors.password}</p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="remember-me" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Remember me
            </label>
          </div>
          <a
            href="#"
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            Forgot password?
          </a>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          size="lg"
          variant="primary"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          rightIcon={<ArrowRightIcon className="h-5 w-5" />}
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-dark-600" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-dark-800 text-gray-500 dark:text-gray-400">
            New to RecyclerBin?
          </span>
        </div>
      </div>

      {/* Sign Up Link */}
      <Button variant="outline" size="lg" fullWidth onClick={() => navigate(ROUTES.SIGNUP)}>
        Create an account
      </Button>
    </motion.div>
  );
};

export default LoginPage;
